/**
 * Demolition-segment detection for the room-merge engine.
 *
 * A "segment" is the demolition unit: the overlap span between two rooms'
 * edges along a shared structure-wall spine. This is the caller-agnostic
 * input the merge engine consumes — the great-room generator produces a
 * list of these from an enumerated option; a manual "select wall → delete"
 * produces exactly one (the clicked edge + the room on its other side).
 *
 * See claude-docs/2026-06-20-scoped-room-merge-retrace.md and the
 * great-room partition-detection algorithm (anti-parallel + span-overlap +
 * geometry-confirm). The geometry-confirm is load-bearing: a single structure
 * wall is a SPINE shared by many rooms (up to 7), so two member rooms can be
 * anti-parallel and span-overlapping on the same spine yet NOT actually face
 * each other (a third room sits between them). Probing both sides of the
 * centerline and requiring each to land in the respective member's floor
 * rejects those false partitions.
 */

const { pointInPolygonXZ } = require('./geometry');

const ANTIPARALLEL_DOT = -0.7; // edge dirs point opposite → rooms on opposite sides
const MIN_OVERLAP_M = 0.15; // shared partition length floor
const FULL_SPINE_TOL_M = 0.25; // overlap within this of the spine length = whole spine
const CONFIRM_PROBE_M = 0.1; // probe this far PAST the wall face (clears floor inset)

/** Long-axis of a structure wall as {origin, dir} in the XZ plane. */
function axisOf(structureWall) {
  const ry = structureWall.rotation?.y || 0;
  return {
    ox: structureWall.center.x,
    oz: structureWall.center.z,
    dx: Math.cos(ry),
    dz: -Math.sin(ry),
  };
}

/** Scalar projection of a point onto a wall axis. */
function projT(p, ax) {
  return (p.x - ax.ox) * ax.dx + (p.z - ax.oz) * ax.dz;
}

/** A room wall's [min,max] span along the structure-wall axis, from its
 *  bottomLeft/bottomRight alignment corners. Null if corners are missing. */
function roomWallSpan(roomWall, ax) {
  const bl = roomWall.alignment?.bottomLeft;
  const br = roomWall.alignment?.bottomRight;
  if (!bl || !br) return null;
  const a = projT(bl, ax);
  const b = projT(br, ax);
  return { min: Math.min(a, b), max: Math.max(a, b) };
}

/** Unit direction of a room wall's edge (bottomLeft → bottomRight). */
function edgeDir(roomWall) {
  const bl = roomWall.alignment?.bottomLeft;
  const br = roomWall.alignment?.bottomRight;
  if (!bl || !br) return null;
  const dx = br.x - bl.x;
  const dz = br.z - bl.z;
  const len = Math.hypot(dx, dz);
  if (len < 1e-9) return null;
  return { x: dx / len, z: dz / len };
}

/** {x,z} corners of a room's first floor polygon, or [] if absent. */
function roomFloorPoly(room) {
  const corners = room.floors?.[0]?.polygonCorners;
  if (!corners?.length) return [];
  return corners.map((p) => ({ x: p.x, z: p.z }));
}

/**
 * Confirm two rooms genuinely face each other across the overlap on a spine:
 * probe just past each wall face (clearing the floor inset) on both sides of
 * the centerline at the overlap midpoint, and require one side to land in
 * roomA's floor and the other in roomB's. Rejects false partitions where a
 * third room sits between the two on the same long spine.
 */
function facesAcross(sw, ax, span, roomA, roomB) {
  const polyA = roomFloorPoly(roomA);
  const polyB = roomFloorPoly(roomB);
  if (polyA.length < 3 || polyB.length < 3) return true; // can't confirm → allow
  const midT = (span.min + span.max) / 2;
  const base = { x: ax.ox + ax.dx * midT, z: ax.oz + ax.dz * midT };
  const off = (sw.dimensions?.z || 0.15) / 2 + CONFIRM_PROBE_M;
  const nx = ax.dz; // perpendicular to the (unit) spine axis
  const nz = -ax.dx;
  const plus = { x: base.x + nx * off, z: base.z + nz * off };
  const minus = { x: base.x - nx * off, z: base.z - nz * off };
  const aPlus = pointInPolygonXZ(plus, polyA);
  const aMinus = pointInPolygonXZ(minus, polyA);
  const bPlus = pointInPolygonXZ(plus, polyB);
  const bMinus = pointInPolygonXZ(minus, polyB);
  return (aPlus && bMinus) || (aMinus && bPlus);
}

/** All {room, wall} pairs whose wall surfaces the given structure wall. */
function roomWallsForStructureWall(rooms, structureWallId) {
  const out = [];
  for (const room of rooms || []) {
    for (const wall of room.walls || []) {
      if (wall?.alignment?.structureWallId === structureWallId) {
        out.push({ room, wall });
      }
    }
  }
  return out;
}

/** Distinct structureWallIds referenced by the given rooms' walls. */
function structureWallIdsForRooms(rooms) {
  const ids = new Set();
  for (const room of rooms || []) {
    for (const wall of room.walls || []) {
      const sid = wall?.alignment?.structureWallId;
      if (sid) ids.add(sid);
    }
  }
  return ids;
}

/**
 * True iff every room that surfaces `structureWallId` is in `memberRoomIds`
 * — i.e. the spine is borne only by member rooms, so demolishing the shared
 * overlap removes the whole spine (no Model-B split needed).
 */
function isStandaloneSpine(structureWallId, rooms, memberRoomIds) {
  const members = new Set(memberRoomIds);
  for (const { room } of roomWallsForStructureWall(rooms, structureWallId)) {
    if (!members.has(room.id)) return false;
  }
  return true;
}

/**
 * Detect the demolition segments internal to a member-room set: for every
 * structure wall surfaced by two member rooms whose edges are anti-parallel
 * and span-overlapping, emit a segment to demolish.
 *
 * Returns Segment[]: { structureWallId, roomIds:[A,B], span:{min,max},
 * standalone }. `span` is in structure-wall-axis coordinates.
 */
function detectSharedSegments(structure, rooms, memberRoomIds) {
  const members = new Set(memberRoomIds);
  const memberRooms = (rooms || []).filter((r) => members.has(r.id));
  const sids = structureWallIdsForRooms(memberRooms);
  const segments = [];

  for (const sid of sids) {
    const sw = (structure.walls || []).find((w) => w.id === sid);
    if (!sw) continue;
    const ax = axisOf(sw);
    const entries = roomWallsForStructureWall(memberRooms, sid);
    // Compare every pair of member edges on this spine.
    for (let i = 0; i < entries.length; i++) {
      for (let j = i + 1; j < entries.length; j++) {
        const a = entries[i];
        const b = entries[j];
        if (a.room.id === b.room.id) continue;
        const dA = edgeDir(a.wall);
        const dB = edgeDir(b.wall);
        if (!dA || !dB) continue;
        const dot = dA.x * dB.x + dA.z * dB.z;
        if (dot > ANTIPARALLEL_DOT) continue; // same side, not a partition
        const spanA = roomWallSpan(a.wall, ax);
        const spanB = roomWallSpan(b.wall, ax);
        if (!spanA || !spanB) continue;
        const min = Math.max(spanA.min, spanB.min);
        const max = Math.min(spanA.max, spanB.max);
        if (max - min < MIN_OVERLAP_M) continue;
        // Geometry-confirm: the two rooms must actually face each other across
        // this overlap (not have a third room between them on the same spine).
        if (!facesAcross(sw, ax, { min, max }, a.room, b.room)) continue;
        // Does the demolished overlap consume the WHOLE structure-wall spine,
        // or only a sub-span? A sub-span leaves surviving wall beyond the
        // overlap (e.g. a member edge longer than the shared partition), which
        // must be kept — that is the Model-B split case. A full-spine overlap
        // can simply delete the spine.
        const swLen = sw.dimensions?.x || 0;
        const coversFullSpine = max - min >= swLen - FULL_SPINE_TOL_M;
        segments.push({
          structureWallId: sid,
          roomIds: [a.room.id, b.room.id],
          span: { min, max },
          standalone: isStandaloneSpine(sid, rooms, memberRoomIds),
          coversFullSpine,
        });
      }
    }
  }
  return segments;
}

module.exports = {
  ANTIPARALLEL_DOT,
  MIN_OVERLAP_M,
  axisOf,
  projT,
  roomWallSpan,
  edgeDir,
  roomWallsForStructureWall,
  structureWallIdsForRooms,
  isStandaloneSpine,
  detectSharedSegments,
};
